/**
 * The one error shape (docs/53 §6.1): `{ error: { code, message, field? } }`. `message` is written for display
 * under the house voice rules (a test lints every default message). Foreign tenant ids return `not_found`,
 * never `forbidden`, so ids don't leak existence (docs/53 §5.2).
 */
import { z } from 'zod';

export const ERROR_CODES = {
  bad_request: { status: 400, message: "We couldn't read that. Nothing was changed." },
  validation_failed: { status: 422, message: "Something in that doesn't look right. Check it and try again." },
  unauthenticated: { status: 401, message: "You've been signed out. Sign in to carry on." },
  step_up_required: { status: 401, message: "For your safety, confirm it's you first." },
  forbidden: { status: 403, message: "Your role can't do this. The owner can." },
  not_found: { status: 404, message: "We couldn't find that." },
  conflict: { status: 409, message: 'That changed while you were looking. Refresh and try again.' },
  gone: { status: 410, message: "That link has expired. Ask for a new one." },
  payload_too_large: { status: 413, message: "That file's too big for us to take." },
  unsupported_media_type: { status: 415, message: "We can't take that kind of file." },
  rate_limited: { status: 429, message: "That's a lot of tries. Wait a minute and try again." },
  code_invalid: { status: 400, message: "That code doesn't match. Check the email and try again." },
  code_expired: { status: 410, message: 'That code has expired. We can send you a new one.' },
  write_frozen: { status: 423, message: "Changes are paused on your account for now. We'll let you know when they're back." },
  work_paused: { status: 402, message: "New work is paused until your card's updated. Your site keeps running." },
  csrf_failed: { status: 403, message: "We couldn't confirm that came from the app. Refresh and try again." },
  internal: { status: 500, message: "Something went wrong on our side. Nothing was lost. Try again in a minute." },
  unavailable: { status: 503, message: "We're down for a few minutes. Nothing was lost. Try again soon." },
} as const satisfies Record<string, { status: number; message: string }>;

export type ErrorCode = keyof typeof ERROR_CODES;
export const ErrorCode = z.enum(Object.keys(ERROR_CODES) as [ErrorCode, ...ErrorCode[]]);

export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string().min(1).max(300),
    /** Dotted path of the offending field for form errors, e.g. `contact.phone`. */
    field: z.string().max(120).optional(),
    /** Echoed request id so a person can quote it to support. */
    requestId: z.string().max(64).optional(),
  }),
});
export type ApiError = z.infer<typeof ApiError>;

export function apiError(code: ErrorCode, opts: { message?: string; field?: string; requestId?: string } = {}): ApiError {
  const e: ApiError['error'] = { code, message: opts.message ?? ERROR_CODES[code].message };
  if (opts.field !== undefined) e.field = opts.field;
  if (opts.requestId !== undefined) e.requestId = opts.requestId;
  return { error: e };
}

export function errorStatus(code: ErrorCode): number {
  return ERROR_CODES[code].status;
}

/** Turn a zod failure into the contract error, naming the first field. */
export function validationError(err: z.ZodError, requestId?: string): ApiError {
  const first = err.issues[0];
  const field = first && first.path.length ? first.path.map(String).join('.') : undefined;
  return apiError('validation_failed', { ...(field ? { field } : {}), ...(requestId ? { requestId } : {}) });
}
